<script setup lang="ts">
import { computed } from 'vue';

import { isRequired } from '@/utils/forms';
import {
  PolicyEnforcement,
  type ProjectCopilotConfigurationV1Snapshot,
  V1SnapshotCommunicationTone,
  V1SnapshotMessagingWindow,
  V1SnapshotPushiness,
  V1SnapshotStrictness,
} from '@factoryfixinc/ats-interfaces';

import MultipleSelectionItems from '@/components/Jobs/CopilotActivation/ProjectCopilotConfiguration/MultipleSelectionItems/MultipleSelectionItems.vue';
import CandidateAiDisclosureField from '@/components/Shared/CandidateAiDisclosureField.vue';
import { isEnumMember } from '@/utils/is-enum-member.util';
import ProjectCopilotConfigurationUtils from '@/utils/project/project-copilot-configuration.utils';

// Props
// ===============================
const props = defineProps<{
  modelValue?: ProjectCopilotConfigurationV1Snapshot;
  candidateAiDisclosurePolicy?: PolicyEnforcement;
}>();

// Emits
// ===============================
const emit = defineEmits<{
  (e: 'update:modelValue', payload: ProjectCopilotConfigurationV1Snapshot): void;
}>();

// Computed properties
// ===============================
const communicationToneItems = computed(() => {
  return [
    {
      title: 'Friendly & personal',
      subtitle: 'Warm and conversational tone',
      icon: 'heart' as const,
      isSelected:
        props.modelValue?.communicationTone === V1SnapshotCommunicationTone.FRIENDLY_PERSONAL,
      tooltipText: '',
      value: `${V1SnapshotCommunicationTone.FRIENDLY_PERSONAL}`,
    },
    {
      title: 'Professional & formal',
      subtitle: 'Business-like and respectful',
      icon: 'briefcase' as const,
      isSelected:
        props.modelValue?.communicationTone === V1SnapshotCommunicationTone.PROFESSIONAL_FORMAL,
      tooltipText: '',
      value: `${V1SnapshotCommunicationTone.PROFESSIONAL_FORMAL}`,
    },
    {
      title: 'Concise & efficient',
      subtitle: 'Clear and to-the-point communication',
      icon: 'target' as const,
      isSelected:
        props.modelValue?.communicationTone === V1SnapshotCommunicationTone.CONCISE_EFFICIENT,
      tooltipText: '',
      value: `${V1SnapshotCommunicationTone.CONCISE_EFFICIENT}`,
    },
  ];
});

const pushinessItems = computed(() => {
  return [
    {
      title: 'Move on quickly',
      subtitle: 'Takes the first "no" at face value and doesn’t follow up after no response',
      icon: 'bolt-fast' as const,
      isSelected: props.modelValue?.pushiness === V1SnapshotPushiness.MOVE_ON_QUICKLY,
      tooltipText: '',
      value: `${V1SnapshotPushiness.MOVE_ON_QUICKLY}`,
    },
    {
      title: 'Pursue with common sense',
      subtitle: 'Makes reasonable attempts to re-engage and explores initial objections',
      icon: 'scale' as const,
      isSelected: props.modelValue?.pushiness === V1SnapshotPushiness.COMMON_SENSE,
      tooltipText: '',
      value: `${V1SnapshotPushiness.COMMON_SENSE}`,
    },
    {
      title: 'Sell the opportunity hard',
      subtitle: 'Works actively to overcome objections and makes multiple follow-up attempts',
      icon: 'megaphone' as const,
      isSelected: props.modelValue?.pushiness === V1SnapshotPushiness.SELL_HARD,
      tooltipText: '',
      value: `${V1SnapshotPushiness.SELL_HARD}`,
    },
  ];
});

const strictnessItems = computed(() => {
  return [
    {
      title: 'Lenient',
      subtitle: 'Consider candidates with transferable skills and potential to learn',
      icon: 'expand' as const,
      isSelected: props.modelValue?.strictness === V1SnapshotStrictness.LENIENT,
      tooltipText: '',
      value: `${V1SnapshotStrictness.LENIENT}`,
    },
    {
      title: 'Balanced',
      subtitle: 'Balance technical requirements with demonstrated mechanical aptitude',
      icon: 'scale' as const,
      isSelected: props.modelValue?.strictness === V1SnapshotStrictness.BALANCED,
      tooltipText: '',
      value: `${V1SnapshotStrictness.BALANCED}`,
    },
    {
      title: 'Strict',
      subtitle: 'Focus on candidates with exact experience and certifications needed',
      icon: 'target' as const,
      isSelected: props.modelValue?.strictness === V1SnapshotStrictness.STRICT,
      tooltipText: '',
      value: `${V1SnapshotStrictness.STRICT}`,
    },
  ];
});

const messagingWindowItems = computed(() => {
  return [
    {
      title: 'Whenever candidate is engaged',
      subtitle: 'Respond as soon as the candidate is active',
      icon: 'checkmark' as const,
      isSelected: props.modelValue?.messagingWindow === V1SnapshotMessagingWindow.WHENEVER_ENGAGED,
      tooltipText: '',
      value: `${V1SnapshotMessagingWindow.WHENEVER_ENGAGED}`,
    },
    {
      title: 'Daytime hours',
      subtitle: 'Message during typical waking hours',
      icon: 'sun' as const,
      isSelected: props.modelValue?.messagingWindow === V1SnapshotMessagingWindow.DAYTIME_HOURS,
      tooltipText: '',
      value: `${V1SnapshotMessagingWindow.DAYTIME_HOURS}`,
    },
    {
      title: 'Business hours',
      subtitle: 'Stick to traditional working hours',
      icon: 'clock' as const,
      isSelected: props.modelValue?.messagingWindow === V1SnapshotMessagingWindow.BUSINESS_HOURS,
      tooltipText: '',
      value: `${V1SnapshotMessagingWindow.BUSINESS_HOURS}`,
    },
  ];
});

const copilotName = computed({
  get: () => props.modelValue?.copilotName || '',
  set: (value) => {
    emit('update:modelValue', {
      ...props.modelValue,
      copilotName: value,
    } as ProjectCopilotConfigurationV1Snapshot);
  },
});

const copilotDescription = computed({
  get: () => (props.modelValue as Record<string, unknown>)?.copilotDescription || '',
  set: (value) => {
    emit('update:modelValue', {
      ...props.modelValue,
      copilotDescription: value,
    } as ProjectCopilotConfigurationV1Snapshot);
  },
});

const includeCandidateAiDisclosure = computed({
  get: () => props.modelValue?.includeAiDisclosure === true,
  set: (value) => {
    emit('update:modelValue', {
      ...(props.modelValue ?? {}),
      includeAiDisclosure: value,
    } as ProjectCopilotConfigurationV1Snapshot);
  },
});

const isCandidateAiDisclosureVisible = computed(
  () =>
    props.candidateAiDisclosurePolicy === PolicyEnforcement.REQUIRED ||
    props.candidateAiDisclosurePolicy === PolicyEnforcement.OPTIONAL,
);

const isCandidateAiDisclosureLocked = computed(
  () => props.candidateAiDisclosurePolicy === PolicyEnforcement.REQUIRED,
);

const candidateAiDisclosureValue = computed({
  get: () => {
    return ProjectCopilotConfigurationUtils.normalizeIncludeAiDisclosure(
      props.candidateAiDisclosurePolicy,
      includeCandidateAiDisclosure.value,
    );
  },
  set: (value: boolean) => {
    includeCandidateAiDisclosure.value = value;
  },
});

function updateSnapshotEnumField<
  K extends keyof ProjectCopilotConfigurationV1Snapshot,
  E extends Record<string, string>,
>(key: K, enumObject: E, value: unknown) {
  if (!isEnumMember(enumObject, value)) {
    return;
  }
  emit('update:modelValue', {
    ...(props.modelValue ?? {}),
    [key]: value,
  });
}

function setCommunicationTone(value: unknown) {
  updateSnapshotEnumField('communicationTone', V1SnapshotCommunicationTone, value);
}

function setPushiness(value: unknown) {
  updateSnapshotEnumField('pushiness', V1SnapshotPushiness, value);
}

function setStrictness(value: unknown) {
  updateSnapshotEnumField('strictness', V1SnapshotStrictness, value);
}

function setMessagingWindow(value: unknown) {
  updateSnapshotEnumField('messagingWindow', V1SnapshotMessagingWindow, value);
}
</script>

<template>
  <section>
    <!-- Copilot Name -->
    <p class="text-shade-88 mb-1 font-sans leading-[27px]">
      <span class="text-base font-extrabold">Recruiter display name</span>
      <span class="text-sm text-shade-800"> (required)</span>
    </p>

    <p class="mb-4 block text-sm font-normal leading-[21px] text-shade-800">
      Your recruiter will use this name when talking to candidates. Human names like "Lucy" or
      "David" work great!
    </p>

    <v-text-field
      data-test="copilot-personality-name"
      v-model="copilotName"
      placeholder="e.g., 'Lucy' or 'David'"
      variant="outlined"
      density="compact"
      autofocus
      validate-on="blur"
      :rules="[isRequired]"
    />

    <CandidateAiDisclosureField
      v-model="candidateAiDisclosureValue"
      :is-visible="isCandidateAiDisclosureVisible"
      :is-locked="isCandidateAiDisclosureLocked"
      class="mb-8"
    />

    <!-- Description -->
    <p class="text-shade-88 mb-1 font-sans leading-[27px]">
      <span class="text-base font-extrabold">Description</span>
      <span class="text-sm text-shade-800"> (optional)</span>
    </p>

    <p class="mb-4 block text-sm font-normal leading-[21px] text-shade-800">
      Describe what your recruiter is best used for
    </p>
    <v-text-field
      v-model="copilotDescription"
      placeholder="e.g. Specializes in skilled trades roles that require industry experience"
      variant="outlined"
      density="compact"
      validate-on="submit"
      :rules="[]"
    />

    <!-- Communication Tone -->
    <MultipleSelectionItems
      title="What tone should your recruiter use with candidates?"
      title-class="!text-base !font-extrabold"
      :items="communicationToneItems"
      class="mb-8"
      @click:select="setCommunicationTone"
    />

    <!-- Pushiness -->
    <MultipleSelectionItems
      title="How persistent should your recruiter be with hesitant candidates?"
      title-class="!text-base !font-extrabold"
      :items="pushinessItems"
      class="mb-8"
      @click:select="setPushiness"
    />

    <!-- Strictness -->
    <MultipleSelectionItems
      title="How strictly should your recruiter evaluate candidates?"
      title-class="!text-base !font-extrabold"
      :items="strictnessItems"
      class="mb-8"
      @click:select="setStrictness"
    />

    <!-- Messaging Window -->
    <MultipleSelectionItems
      title="When should your recruiter message with candidates?"
      title-class="!text-base !font-extrabold"
      :items="messagingWindowItems"
      class="mb-8"
      @click:select="setMessagingWindow"
    />
  </section>
</template>

<style lang="scss" scoped>
.v-input :deep(.v-field),
.v-input--horizontal :deep(.v-field) {
  border-radius: 6px;

  .v-combobox__selection {
    @apply font-sans text-sm font-normal leading-[21px] text-shade-880;
  }

  .v-field__outline {
    @apply text-tint-80;
  }
  input {
    @apply font-sans text-sm font-normal leading-[21px] text-shade-880;
  }
  input::placeholder {
    @apply text-shade-800;
    opacity: 1;
  }

  &:hover {
    .v-field__outline {
      @apply text-shade-840;
    }
  }
}

.v-input :deep(.v-field.v-field--focused),
.v-input--horizontal :deep(.v-field.v-field--focused) {
  .v-field__outline {
    @apply text-highlight-500;
  }
}

.v-input :deep(.v-field.v-field--error),
.v-input--horizontal :deep(.v-field.v-field--error) {
  .v-field__outline {
    @apply text-critical-500;
  }
}
</style>
